import { useEffect } from 'react';
import { CircleAlert } from 'lucide-react';
import { usePendingSaves } from '@/stores/taskOptimistic';
import { Spinner } from '@/components/ui/spinner';

/**
 * A small "Saving…" pill (bottom left of the page) while task changes are still on their way to
 * the server, and a browser warning if the tab is closed before they land. Messages that
 * failed to send count as unsaved too.
 */
export function TaskSavingIndicator() {
  const { saving, failed } = usePendingSaves();
  const unsaved = saving + failed;

  useEffect(() => {
    if (!unsaved) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Older browsers need a returnValue to show the prompt; the text itself isn't shown.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved]);

  if (!unsaved) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 left-4 z-50 flex items-center gap-1.5 rounded-full border bg-background px-3 py-1.5 text-xs font-medium shadow-md md:left-[calc(var(--sidebar-width)+1rem)]"
    >
      {saving ? (
        <>
          <Spinner size="sm" className="text-muted-foreground" />
          Saving…
        </>
      ) : (
        <>
          <CircleAlert className="size-3.5 text-destructive" />
          {failed === 1 ? '1 message not sent' : `${failed} messages not sent`}
        </>
      )}
    </div>
  );
}
